/**
 * PII redaction for URLs. The SDK redacts query-string values whose key looks
 * sensitive *before* including the URL in any event property. Spec §10.4.
 */

const REDACTED_KEYS = new Set([
	'email',
	'e-mail',
	'mail',
	'phone',
	'tel',
	'telephone',
	'password',
	'pass',
	'pwd',
	'token',
	'jwt',
	'api_key',
	'apikey',
	'secret',
	'cc',
	'card',
	'ccn',
	'cvv',
	'ssn',
	'dni',
	'cpf',
	'rut',
]);

const REDACTED_VALUE = '<redacted>';

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const CC_RE = /\b(?:\d[ -]*?){13,19}\b/;

/** Redact suspicious values in a URL's query string. Idempotent. */
export function redactUrl(input: string): string {
	if (!input || typeof input !== 'string') return input;
	// Only redact recognisable absolute URLs (http/https). Anything else gets
	// passed through — the SDK only ever feeds this function values from
	// location.href, document.referrer, or anchor.href, all of which are
	// absolute when present.
	if (!/^https?:\/\//i.test(input)) return input;
	let url: URL;
	try {
		url = new URL(input);
	} catch {
		return input;
	}
	const params = url.searchParams;
	const keys: string[] = [];
	params.forEach((_, k) => keys.push(k));
	for (const key of keys) {
		const lower = key.toLowerCase();
		if (REDACTED_KEYS.has(lower)) {
			params.set(key, REDACTED_VALUE);
			continue;
		}
		const value = params.get(key) || '';
		if (EMAIL_RE.test(value) || CC_RE.test(value)) {
			params.set(key, REDACTED_VALUE);
		}
	}
	return url.toString();
}

/** Heuristic: does this string look like PII that we shouldn't accept raw? */
export function looksLikePII(value: unknown): boolean {
	if (typeof value !== 'string' || value.length < 5) return false;
	return EMAIL_RE.test(value) || /^[+]?\d[\d\s().-]{7,}$/.test(value) || CC_RE.test(value);
}
